import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useEffect, useCallback } from 'react'
import ClientIndexView from './ClientIndexView'
import usePagination from '../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../constants/constant'

const ClientIndexApp = () => {
  const [isFetching, setIsFetching] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)
  const [data, setData] = useState(null)
  const [title] = useState('Clientes')
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [filterQuery, setFilterQuery] = useState('')
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [resetReactFilters, setResetReactFilters] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState([])
  const [clientsData, setClientsData] = useState([])
  const [priceListsData, setPriceListsData] = useState([])
  const [triggerSearch, setTriggerSearch] = useState(false)

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })

  const getOptionId = (optionValue) => {
    const [id] = String(optionValue).split(' - ')
    return id
  }

  const updateAllSelectedOptions = (identifier, selectedOptions) => {
    const selectedOptionsArray = Array.from(selectedOptions)
    setAllSelectedOptions((prev) => {
      const newOptions = new Map(prev)
      newOptions.set(identifier, selectedOptionsArray)
      return newOptions
    })
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    buildFiltersQuery()
  }

  const buildFiltersQuery = () => {
    let clientArray = []
    let pricelistArray = []
    let queryParam = ''
    for (const [key, value] of allSelectedOptions) {
      if (value.length !== 0) {
        if (key === 'CLIENTE') {
          value.forEach((v) => {
            const clientId = getOptionId(v)
            if (clientId) clientArray.push(clientId)
          })
          queryParam += `&clientes=${encodeURIComponent(clientArray.join(','))}`
        } else if (key === 'LISTA DE PRECIO ASIGNADA') {
          value.forEach((v) => {
            const pricelistId = getOptionId(v)
            if (pricelistId) pricelistArray.push(pricelistId)
          })
          queryParam += `&pricelist_id=${encodeURIComponent(pricelistArray.join(','))}`
        }
      }
    }
    setFilterQuery(queryParam)
  }

  const resetFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setSearchInput('')
    setSearchTerm('')
    setTriggerSearch((prev) => !prev)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
    setResetReactFilters((prev) => !prev)
  }

  const isAnyFilterSelected = () =>
    Array.from(allSelectedOptions.values()).some((options) => options.length > 0)

  const fetchFilterInfoFromServer = useCallback(async (loadingState = true, signal) => {
    try {
      if (loadingState) setFetchDataStatus((prev) => ({ ...prev, filters: true }))
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/clients?client_names_only=true`, {
        method: 'GET',
        signal
      })
      if (!response || response.error) {
        console.error('Error en fetchFilterInfoFromServer:', response?.status || 'Fetch error')
        return
      }
      setClientsData(response.clients)
      setPriceListsData(response.price_lists)
      setFilters([
        {
          key: 'CLIENTE',
          value: response.clients?.map((c) => `${c.id} - ${c.name}`)
        },
        {
          key: 'LISTA DE PRECIO ASIGNADA',
          value: response.price_lists
            ?.filter((p) => p.name && p.id)
            .map((p) => `${p.id} - ${p.name}`)
        }
      ])
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error:', error)
    } finally {
      setFetchDataStatus((prev) => ({ ...prev, filters: false }))
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchFilterInfoFromServer(true, controller.signal)
    return () => controller.abort('Aborted fetchFilterInfoFromServer on cleanup.')
  }, [fetchFilterInfoFromServer])

  const fetchDataFromServer = useCallback(
    async (signal) => {
      if (initialLoad) setIsFetching(true)
      const { per_page, current_page } = pagination
      try {
        const response = await authFetch(
          `${BASE_BACKEND_URL}/clients?page=${current_page}&per_page=${per_page}${filterQuery ? '&filter_search=true' : ''}${filterQuery}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`,
          {
            method: 'GET',
            signal,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          }
        )
        if (!response.ok) return
        const result = await response.json()
        setPagination((prev) => ({
          ...prev,
          total_pages: Math.ceil(result.count / prev.per_page)
        }))
        setData(result.table)
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Error:', error)
        setData([])
      } finally {
        setIsFetching(false)
        setInitialLoad(false)
      }
    },
    [pagination.current_page, filterQuery, searchTerm]
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchDataFromServer(controller.signal)
    return () => controller.abort('Aborted fetchDataFromServer on cleanup.')
  }, [pagination.current_page, triggerSearch, filterQuery])

  useEffect(() => {
    if (allSelectedOptions.size > 0) buildFiltersQuery()
  }, [allSelectedOptions])

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
    setPagination((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setSearchTerm(searchInput)
      setTriggerSearch((prev) => !prev)
    }
  }

  const isFullyLoaded = !isFetching && !fetchDataStatus.filters

  return (
    <ClientIndexView
      data={data}
      pagination={pagination}
      handlePaginationClick={handlePaginationClick}
      filters={filters}
      updateAllSelectedOptions={updateAllSelectedOptions}
      resetReactFilters={resetReactFilters}
      resetFilters={resetFilters}
      onSearch={handleSearch}
      onSearchKeyPress={handleEnterSearch}
      isAnyFilterSelected={isAnyFilterSelected()}
      isFetching={!isFullyLoaded}
      title={title}
      searchInput={searchInput}
    />
  )
}

export default ClientIndexApp
